import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
} from "@mui/material";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import styled from "@emotion/styled";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import ContentBox from "../../../components/ui/ContentBox";
import Api from "../../../api/Api";
import { Urls } from "../../../api/Urls";
import { useAuthContext } from "../../../hooks/useAuthContext";
import { ToastContainer, toast } from "react-toastify";

const TYPE_LABELS = {
  fixed_monthly: "Fixed Monthly",
  per_hour: "Per Hour",
  per_session: "Per Session",
  per_player: "Per Player",
  revenue_percentage: "Revenue %",
};

const SCOPE_LABELS = {
  all: "All Lessons",
  group_lesson: "Group Lessons",
  private_lesson: "Private Lessons",
  program: "Program",
};

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "white",
    paddingTop: "8px",
    paddingBottom: "8px",
    letterSpacing: "1px",
    color: "#4B465C",
    fontSize: 13,
    fontWeight: 500,
  },
  [`&.${tableCellClasses.body}`]: {
    background: "white",
    fontSize: 14,
    color: "#666D83",
    fontWeight: "500",
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:last-child td, &:last-child th": { border: 0 },
  ":hover": { filter: "brightness(0.95)" },
}));

const StyledTable = styled(Table)({ borderRadius: "0px" });

function CoachSalaryConfig({ id }) {
  const { user } = useAuthContext();
  const access_token = user?.access_token;

  const [allComponents, setAllComponents] = useState([]);
  const [coachConfigs, setCoachConfigs] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    document.title = "BTA admin - Coach Salary Config";
    loadData();
  }, []);

  const loadData = () => {
    setLoading(true);
    Promise.all([
      Api.get(Urls.GET_SALARY_COMPONENTS, {
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${access_token}` },
        params: { all: 1, is_active: 1 },
      }),
      Api.get(Urls.GET_COACH_SALARY_CONFIGS + id + "/salaryConfigs", {
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${access_token}` },
      }),
    ])
      .then(([componentsRes, configsRes]) => {
        const components = componentsRes?.data?.data || [];
        const configs = configsRes?.data?.data || [];

        setAllComponents(components);

        // Build a map from salary_component_id -> value
        // Pre-fill with default values, then overlay with coach overrides
        const configMap = {};
        components.forEach((comp) => {
          configMap[comp.id] = comp.default_value;
        });
        configs.forEach((c) => {
          configMap[c.salary_component_id] = c.value;
        });
        setCoachConfigs(configMap);
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        toast.error(err?.response?.data?.message || "Error loading salary configuration");
      });
  };

  const handleValueChange = (componentId, value) => {
    setCoachConfigs((prev) => ({
      ...prev,
      [componentId]: value,
    }));
  };

  const handleSave = () => {
    setSaving(true);

    // Build configs array: only include components where value differs from default
    const configs = allComponents
      .filter((c) => coachConfigs[c.id] !== undefined && coachConfigs[c.id] !== "")
      .map((c) => ({
        salary_component_id: c.id,
        value: parseFloat(coachConfigs[c.id]),
      }));

    Api.post(
      Urls.STORE_COACH_SALARY_CONFIGS + id + "/salaryConfigs",
      { configs },
      { headers: { "Content-Type": "application/json", Authorization: `Bearer ${access_token}` } }
    )
      .then(() => {
        toast.success("Salary configuration saved successfully");
        setSaving(false);
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || "Error saving configuration");
        setSaving(false);
      });
  };

  const getScopeLabel = (component) => {
    if (component.scope_type === "program" && component.program) {
      return `Program: ${component.program.name}`;
    }
    return SCOPE_LABELS[component.scope_type] || component.scope_type;
  };

  return (
    <Box display="flex" flexDirection="column" gap="26px">
      <ToastContainer />
      <Box>
        <Typography fontSize="22px" fontWeight="500" lineHeight="30px" color="#4B465C">
          Salary Configuration
        </Typography>
        <Typography fontSize="14px" color="#6F6B7D" mt="4px">
          Override default rates for this coach. Values are pre-filled with defaults.
        </Typography>
      </Box>

      <ContentBox>
        <Box display="flex" flexDirection="column" gap="16px">
          {loading && (
            <Box display="flex" justifyContent="center">
              <CircularProgress />
            </Box>
          )}

          {!loading && allComponents.length === 0 && (
            <Typography textAlign="center" color="#666D83">
              No salary components configured. Go to Salary page to create components first.
            </Typography>
          )}

          {!loading && allComponents.length > 0 && (
            <>
              <TableContainer sx={{ borderRadius: "0px", border: "1px solid #DBDADE" }}>
                <StyledTable>
                  <TableHead>
                    <TableRow>
                      <StyledTableCell>COMPONENT</StyledTableCell>
                      <StyledTableCell>TYPE</StyledTableCell>
                      <StyledTableCell>SCOPE</StyledTableCell>
                      <StyledTableCell align="center">DEFAULT</StyledTableCell>
                      <StyledTableCell align="center">COACH OVERRIDE</StyledTableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {allComponents.map((c) => (
                      <StyledTableRow key={c.id}>
                        <StyledTableCell>{c.name}</StyledTableCell>
                        <StyledTableCell>{TYPE_LABELS[c.type] || c.type}</StyledTableCell>
                        <StyledTableCell>{getScopeLabel(c)}</StyledTableCell>
                        <StyledTableCell align="center">
                          {c.type === "revenue_percentage" ? `${c.default_value}%` : `${c.default_value} BHD`}
                        </StyledTableCell>
                        <StyledTableCell align="center">
                          <TextField
                            size="small"
                            type="number"
                            inputProps={{ min: 0, step: 0.01 }}
                            sx={{ width: "120px" }}
                            placeholder={c.default_value}
                            value={coachConfigs[c.id] ?? ""}
                            onChange={(e) => handleValueChange(c.id, e.target.value)}
                          />
                        </StyledTableCell>
                      </StyledTableRow>
                    ))}
                  </TableBody>
                </StyledTable>
              </TableContainer>

              <Box display="flex" justifyContent="flex-end">
                <Button variant="primary_active" onClick={handleSave} disabled={saving}>
                  {saving ? <CircularProgress size={20} /> : "Save Configuration"}
                </Button>
              </Box>
            </>
          )}
        </Box>
      </ContentBox>
    </Box>
  );
}

export default CoachSalaryConfig;
